/**
 * taxConfig.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import { getLineUnitPriceCents } from './cartPricing';
import { expandItemsForTaxCalculation } from './bundleTax';
import { TAX_EXEMPT_CATEGORIES, POINT_OF_SALE_REBATE_CATEGORIES } from './taxCategories';

// Tax calculation utilities

export const TAX_RATES = {
  // US States
  'US': {
    'AL': 0.04, // Alabama
    'AK': 0.00, // Alaska (no state sales tax)
    'AZ': 0.056, // Arizona
    'AR': 0.065, // Arkansas
    'CA': 0.0725, // California
    'CO': 0.029, // Colorado
    'CT': 0.0635, // Connecticut
    'DE': 0.00, // Delaware (no state sales tax)
    'FL': 0.06, // Florida
    'GA': 0.04, // Georgia
    'HI': 0.04, // Hawaii
    'ID': 0.06, // Idaho
    'IL': 0.0625, // Illinois
    'IN': 0.07, // Indiana
    'IA': 0.06, // Iowa
    'KS': 0.065, // Kansas
    'KY': 0.06, // Kentucky
    'LA': 0.045, // Louisiana
    'ME': 0.055, // Maine
    'MD': 0.06, // Maryland
    'MA': 0.0625, // Massachusetts
    'MI': 0.06, // Michigan
    'MN': 0.06875, // Minnesota
    'MS': 0.07, // Mississippi
    'MO': 0.04225, // Missouri
    'MT': 0.00, // Montana (no state sales tax)
    'NE': 0.055, // Nebraska
    'NV': 0.0685, // Nevada
    'NH': 0.00, // New Hampshire (no state sales tax)
    'NJ': 0.06625, // New Jersey
    'NM': 0.05125, // New Mexico
    'NY': 0.08, // New York
    'NC': 0.0475, // North Carolina
    'ND': 0.05, // North Dakota
    'OH': 0.0575, // Ohio
    'OK': 0.045, // Oklahoma
    'OR': 0.00, // Oregon (no state sales tax)
    'PA': 0.06, // Pennsylvania
    'RI': 0.07, // Rhode Island
    'SC': 0.06, // South Carolina
    'SD': 0.045, // South Dakota
    'TN': 0.07, // Tennessee
    'TX': 0.0625, // Texas
    'UT': 0.061, // Utah
    'VT': 0.06, // Vermont
    'VA': 0.053, // Virginia
    'WA': 0.065, // Washington
    'WV': 0.06, // West Virginia
    'WI': 0.05, // Wisconsin
    'WY': 0.04, // Wyoming
    'DC': 0.06, // Washington DC
  },
  // Canadian Provinces
  'CA': {
    'AB': 0.05, // Alberta (GST only)
    'BC': 0.12, // British Columbia (GST + PST)
    'MB': 0.12, // Manitoba (GST + RST)
    'NB': 0.15, // New Brunswick (HST)
    'NL': 0.15, // Newfoundland and Labrador (HST)
    'NS': 0.14, // Nova Scotia (HST) - 14% as of April 1, 2025 (5% GST + 9% provincial)
    'ON': 0.13, // Ontario (HST)
    'PE': 0.15, // Prince Edward Island (HST)
    'QC': 0.14975, // Quebec (GST + QST)
    'SK': 0.11, // Saskatchewan (GST + PST)
    'NT': 0.05, // Northwest Territories (GST only)
    'NU': 0.05, // Nunavut (GST only)
    'YT': 0.05, // Yukon (GST only)
  },
  // UK
  'UK': {
    'EN': 0.20, // England (VAT)
    'SC': 0.20, // Scotland (VAT)
    'WA': 0.20, // Wales (VAT)
    'NI': 0.20, // Northern Ireland (VAT)
  },
  // Australia
  'AU': {
    'NSW': 0.10, // New South Wales (GST)
    'VIC': 0.10, // Victoria (GST)
    'QLD': 0.10, // Queensland (GST)
    'WA': 0.10, // Western Australia (GST)
    'SA': 0.10, // South Australia (GST)
    'TAS': 0.10, // Tasmania (GST)
    'ACT': 0.10, // Australian Capital Territory (GST)
    'NT': 0.10, // Northern Territory (GST)
  },
  // Germany
  'DE': {
    'BW': 0.19, // Baden-Württemberg (VAT)
    'BY': 0.19, // Bavaria (VAT)
    'BE': 0.19, // Berlin (VAT)
    'BB': 0.19, // Brandenburg (VAT)
    'HB': 0.19, // Bremen (VAT)
    'HH': 0.19, // Hamburg (VAT)
    'HE': 0.19, // Hesse (VAT)
    'MV': 0.19, // Mecklenburg-Vorpommern (VAT)
    'NI': 0.19, // Lower Saxony (VAT)
    'NW': 0.19, // North Rhine-Westphalia (VAT)
    'RP': 0.19, // Rhineland-Palatinate (VAT)
    'SL': 0.19, // Saarland (VAT)
    'SN': 0.19, // Saxony (VAT)
    'ST': 0.19, // Saxony-Anhalt (VAT)
    'SH': 0.19, // Schleswig-Holstein (VAT)
    'TH': 0.19, // Thuringia (VAT)
  },
  // France
  'FR': {
    'FR': 0.20, // France (VAT)
  },
  // Japan
  'JP': {
    'JP': 0.10, // Japan (Consumption Tax)
  }
};

export { TAX_EXEMPT_CATEGORIES, POINT_OF_SALE_REBATE_CATEGORIES };

// Calculate tax for a given location and amount
// Calculate tax for a given location and amount
export const calculateTax = (subtotal, country, stateProvince = null, items = [], taxSettings = [], shippingCost = 0) => {
  console.log('💰 calculateTax called with:', { subtotal, country, stateProvince, itemsCount: items.length, taxSettingsCount: taxSettings?.length, shippingCost });

  // Default to hardcoded rates if no settings provided
  let generalRate = 0;
  let booksRate = 0;
  let shippingTaxRate = 0;

  // Try to find in dynamic settings first
  if (taxSettings && taxSettings.length > 0) {
    const setting = taxSettings.find(s =>
      s.zone === country &&
      (s.province_code === stateProvince || (s.province_code === null && !stateProvince))
    );

    if (setting) {
      generalRate = setting.general_rate;
      booksRate = setting.books_rate;
      shippingTaxRate = setting.shipping_tax_rate;
    } else if (country === 'CA' && stateProvince) {
      // Fallback for CA if not found in settings but is a province
      const countryRates = TAX_RATES[country];
      generalRate = countryRates ? (countryRates[stateProvince] || 0) : 0;
      booksRate = 0.05; // Default book rate
      shippingTaxRate = generalRate; // Default shipping tax to general rate
    }
  } else {
    // Fallback to hardcoded rates
    const countryRates = TAX_RATES[country];
    if (countryRates) {
      generalRate = stateProvince ? countryRates[stateProvince] : (country === 'CA' ? 0.05 : 0);
      booksRate = 0.05; // Default book rate for CA
      shippingTaxRate = generalRate;
    }
  }

  // International/US orders (non-Canadian) usually NO TAX in this system unless configured
  // The user data says US/INTL are free of taxes, which matches general_rate=0 in seed
  if (generalRate === 0 && booksRate === 0 && shippingTaxRate === 0) {
    return {
      tax: 0,
      rate: 0,
      breakdown: [],
      isInternational: country !== 'CA'
    };
  }

  let totalTax = 0;
  let breakdown = [];

  // Separate items by tax treatment (bundles may expand via bundleTaxAllocations)
  const taxExemptItems = [];
  const bookItems = [];
  const regularTaxableItems = [];

  const taxItems = expandItemsForTaxCalculation(items);

  taxItems.forEach(item => {
    // Normalize category and name (remove emojis, trim, lowercase)
    const category = (item.category || '').toLowerCase().replace(/[\u{1F300}-\u{1F9FF}]/gu, '').trim();
    const name = (item.name || '').toLowerCase().replace(/[\u{1F300}-\u{1F9FF}]/gu, '').trim();
    const type = (item.type || '').toLowerCase().replace(/[\u{1F300}-\u{1F9FF}]/gu, '').trim();

    // Check for product-level tax override
    const taxType = item.tax_type || 'auto';

    const isDigitalAlbum =
      item.is_digital_album === 1 ||
      item.is_digital_album === true ||
      item.is_digital_album === '1';

    // Digital music downloads: full HST/GST — never book point-of-sale rebate
    if (isDigitalAlbum) {
      regularTaxableItems.push(item);
    } else if (taxType === 'exempt') {
      // Force tax exempt
      taxExemptItems.push(item);
    } else if (taxType === 'gst' || (taxType === 'auto' && POINT_OF_SALE_REBATE_CATEGORIES.some(rebate =>
      category.includes(rebate) || name.includes(rebate) || type.includes(rebate)
    ))) {
      // Books
      bookItems.push(item);
    } else if (taxType === 'custom' && item.tax_rate != null) {
      // Custom tax rate
      item._customTaxRate = parseFloat(item.tax_rate) / 100;
      regularTaxableItems.push(item);
    } else {
      // Full provincial rate
      regularTaxableItems.push(item);
    }
  });

  // BOOKS: Use books_rate (usually 5% GST)
  if (bookItems.length > 0) {
    const bookAmount = bookItems.reduce((sum, item) => {
      const itemPrice = getLineUnitPriceCents(item);
      return sum + (itemPrice * item.quantity);
    }, 0);

    const bookTax = Math.round(bookAmount * booksRate);
    totalTax += bookTax;

    if (bookTax > 0) {
      breakdown.push({
        type: 'gst_books',
        rate: booksRate,
        amount: bookTax,
        description: `Tax on Books (${(booksRate * 100).toFixed(0)}%)`,
        items: bookItems.length
      });
    }
  }

  // NON-BOOKS: Use general_rate
  if (regularTaxableItems.length > 0) {
    const standardRateItems = regularTaxableItems.filter(item => !item._customTaxRate);
    const customRateItems = regularTaxableItems.filter(item => item._customTaxRate);

    // Standard rate items
    if (standardRateItems.length > 0) {
      const regularAmount = standardRateItems.reduce((sum, item) => {
        const itemPrice = getLineUnitPriceCents(item);
        return sum + (itemPrice * item.quantity);
      }, 0);

      const regularTax = Math.round(regularAmount * generalRate);
      totalTax += regularTax;

      if (regularTax > 0) {
        breakdown.push({
          type: 'sales_tax',
          rate: generalRate,
          amount: regularTax,
          description: `${stateProvince || country} Sales Tax (${(generalRate * 100).toFixed(0)}%)`,
          items: standardRateItems.length
        });
      }
    }

    // Custom rate items
    customRateItems.forEach(item => {
      const itemPrice = getLineUnitPriceCents(item);
      const itemAmount = itemPrice * item.quantity;
      const itemTax = Math.round(itemAmount * item._customTaxRate);
      totalTax += itemTax;

      if (itemTax > 0) {
        breakdown.push({
          type: 'custom_tax',
          rate: item._customTaxRate,
          amount: itemTax,
          description: `Tax on ${item.name} (${(item._customTaxRate * 100).toFixed(0)}%)`,
          items: 1
        });
      }
    });
  }

  // SHIPPING TAX
  if (shippingCost > 0 && shippingTaxRate > 0) {
    const shippingTax = Math.round(shippingCost * shippingTaxRate);
    totalTax += shippingTax;

    breakdown.push({
      type: 'shipping_tax',
      rate: shippingTaxRate,
      amount: shippingTax,
      description: `Tax on Shipping (${(shippingTaxRate * 100).toFixed(0)}%)`,
      items: 0
    });
  }

  return {
    tax: totalTax,
    rate: breakdown.length > 0 ? (totalTax / (subtotal + shippingCost)) : 0, // Approximate rate
    breakdown: breakdown,
    hasBooks: bookItems.length > 0
  };
};

// Get tax rate for a specific location
export const getTaxRate = (country, stateProvince = null) => {
  const countryRates = TAX_RATES[country];
  if (!countryRates) return 0;

  return stateProvince ? countryRates[stateProvince] : 0;
};

// Check if a product category is tax-exempt (NO tax charged)
export const isTaxExempt = (category) => {
  const categoryLower = category?.toLowerCase() || '';
  return TAX_EXEMPT_CATEGORIES.some(exempt => categoryLower.includes(exempt));
};

// Check if a product is eligible for point-of-sale rebate (reduced tax - GST only)
export const isPointOfSaleRebateEligible = (category, productName = '', country = 'CA') => {
  if (country !== 'CA') return false;

  const categoryLower = category?.toLowerCase() || '';
  const nameLower = productName?.toLowerCase() || '';

  return POINT_OF_SALE_REBATE_CATEGORIES.some(rebate =>
    categoryLower.includes(rebate) || nameLower.includes(rebate)
  );
};

// Format tax rate as percentage
export const formatTaxRate = (rate) => {
  return `${(rate * 100).toFixed(2)}%`;
};

// Get all available states/provinces for a country
export const getStatesProvinces = (country) => {
  const countryRates = TAX_RATES[country];
  if (!countryRates) return [];

  return Object.keys(countryRates).map(code => ({
    code,
    name: getStateProvinceName(country, code)
  }));
};

// Get display name for state/province
export const getStateProvinceName = (country, code) => {
  const stateNames = {
    'US': {
      'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas',
      'CA': 'California', 'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware',
      'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho',
      'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas',
      'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
      'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi',
      'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada',
      'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York',
      'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
      'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
      'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah',
      'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia',
      'WI': 'Wisconsin', 'WY': 'Wyoming', 'DC': 'Washington DC'
    },
    'CA': {
      'AB': 'Alberta', 'BC': 'British Columbia', 'MB': 'Manitoba', 'NB': 'New Brunswick',
      'NL': 'Newfoundland and Labrador', 'NS': 'Nova Scotia', 'ON': 'Ontario',
      'PE': 'Prince Edward Island', 'QC': 'Quebec', 'SK': 'Saskatchewan',
      'NT': 'Northwest Territories', 'NU': 'Nunavut', 'YT': 'Yukon'
    }
  };

  return stateNames[country]?.[code] || code;
};
